import React, { ChangeEvent, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  CardMedia,
  Input,
} from "@mui/material";
import EditText from "../../components/editText";
import AddFaq from "../../Modals/addFaq";
import AddIcon from "@mui/icons-material/Add";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetCMSQuery, useUpdateCMSMutation, useGetFaqListQuery, useDeleteFaqMutation, FaqItem } from "../../store/api/manageAPI";
import Loader from "../../constants/Loader";
import { showToast, showError } from "../../constants/toasts";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import Pagination from "../../components/Panigation";
import moment from "moment";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCms = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  // Form states
  const [privacyPolicy, setPrivacyPolicy] = useState<string>("");
  const [termsAndConditions, setTermsAndConditions] = useState<string>("");
  const [aboutUs, setAboutUs] = useState<string>("");
  const [legal, setLegal] = useState<string>("");
  const [dataDeletionPolicy, setDataDeletionPolicy] = useState<string>("");

  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setSearchTerm(""); // Clear search on tab change
  };

  const [openModal, setOpenModal] = useState(false);
  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEdit(false);
    setSelectedFaq(null);
  };

  // Contact Support states
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({});
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [email, setEmail] = useState("");
  const [cmsId, setCmsId] = useState<string>("");

  // API hooks
  const { data, isLoading, error, refetch } = useGetCMSQuery();
  const [updateCMS, { isLoading: isUpdating }] = useUpdateCMSMutation();

  // FAQ states & API
  const [faqPage, setFaqPage] = useState(1);
  const [faqRowsPerPage, setFaqRowsPerPage] = useState(10);
  const [selectedFaq, setSelectedFaq] = useState<FaqItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: faqData, isLoading: isFaqLoading } = useGetFaqListQuery({
    page: faqPage,
    limit: faqRowsPerPage,
    search: searchTerm,
  }, { skip: valueTabs !== 4 });

  const [deleteFaq, { isLoading: isDeletingFaq }] = useDeleteFaqMutation();

  const handleEditFaq = (faq: FaqItem) => {
    setSelectedFaq(faq);
    setIsEdit(true);
    setOpenModal(true);
  };

  const handleDeleteFaqClick = (faq: FaqItem) => {
    setSelectedFaq(faq);
    setDeleteModalOpen(true);
  };

  const handleConfirmDeleteFaq = async () => {
    if (selectedFaq) {
      try {
        await deleteFaq({ id: selectedFaq._id }).unwrap();
        showToast("FAQ deleted successfully");
        setDeleteModalOpen(false);
        setSelectedFaq(null);
      } catch (err: any) {
        showError(err?.data?.message || "Failed to delete FAQ");
      }
    }
  };

  // Reset page when search changes
  useEffect(() => {
    setFaqPage(1);
  }, [searchTerm]);

  // Populate form when data is fetched
  useEffect(() => {
    if (data?.data) {
      const cmsData = data.data;

      // Store CMS ID
      if (cmsData._id) {
        setCmsId(cmsData._id);
      }

      // Contact Support
      if (cmsData.contactSupport) {
        setEmail(cmsData.contactSupport.email || "");
        const phoneNum = cmsData.contactSupport.phone || "";
        const country = cmsData.contactSupport.countryCode || "+91";
        // Store phone number without country code
        setPhone(phoneNum);
        setCountryCode(country);
      }

      // Other fields
      setPrivacyPolicy(cmsData.privacyPolicy || "");
      setTermsAndConditions(cmsData.termsAndConditions || "");
      setAboutUs(cmsData.aboutUs || "");
      setLegal(cmsData.legal || "");
      setDataDeletionPolicy(cmsData.dataDeletionPolicy || "");
    }
  }, [data]);

  // Show error message if API call fails
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.data?.message || "Failed to fetch CMS data. Please try again.";
      showError(errorMessage);
    }
  }, [error]);

  // Validation helpers
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    // Phone number is stored without country code, so just check length
    return phone.trim().length >= 10;
  };

  const clearError = (fieldName: keyof typeof errors) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // Handle Contact Support Save
  const handleContactSupportSave = async () => {
    const newErrors: { email?: string; phone?: string } = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await updateCMS({
        contactSupport: {
          email: email.trim(),
          phone: phone.trim(), // Phone is already without country code
          countryCode: countryCode.trim(),
        },
      }).unwrap();

      const successMessage = response?.message || "Contact support information updated successfully";
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating contact support:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update contact support. Please try again.";
      showError(errorMessage);
    }
  };

  // Handle Privacy Policy Save
  const handlePrivacyPolicySave = async () => {
    if (!privacyPolicy.trim()) {
      showError("Privacy Policy is required");
      return;
    }

    try {
      const response = await updateCMS({
        contactSupport: {
          email: email.trim(),
          phone: phone.trim(), // Phone is already without country code
          countryCode: countryCode.trim(),
        },
        privacyPolicy: privacyPolicy.trim(),
      }).unwrap();

      const successMessage = response?.message || "Privacy Policy updated successfully";
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating privacy policy:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update privacy policy. Please try again.";
      showError(errorMessage);
    }
  };

  // Handle Terms & Conditions Save
  const handleTermsAndConditionsSave = async () => {
    if (!termsAndConditions.trim()) {
      showError("Terms & Conditions is required");
      return;
    }

    try {
      const response = await updateCMS({
        contactSupport: {
          email: email.trim(),
          phone: phone.trim(), // Phone is already without country code
          countryCode: countryCode.trim(),
        },
        termsAndConditions: termsAndConditions.trim(),
      }).unwrap();

      const successMessage = response?.message || "Terms & Conditions updated successfully";
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating terms and conditions:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update terms and conditions. Please try again.";
      showError(errorMessage);
    }
  };

  // Handle About Us Save
  const handleAboutUsSave = async () => {
    if (!aboutUs.trim()) {
      showError("About Us is required");
      return;
    }

    try {
      const response = await updateCMS({
        contactSupport: {
          email: email.trim(),
          phone: phone.trim(), // Phone is already without country code
          countryCode: countryCode.trim(),
        },
        aboutUs: aboutUs.trim(),
      }).unwrap();

      const successMessage = response?.message || "About Us updated successfully";
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating about us:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update about us. Please try again.";
      showError(errorMessage);
    }
  };

  const [isEdit, setIsEdit] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [image, setImage] = useState<string>("");
  const [image2, setImage2] = useState<string>("");

  const [fileName, setFileName] = useState<string>("");
  const [color, setColor] = useState("#0CD0C6");
  return (
    <>
      <Loader isLoad={isLoading || isUpdating || isFaqLoading || isDeletingFaq} />
      <div className="main_title">
        <h1>Manage CMS</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage CMS
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Contact Support" {...a11yProps(0)} />
              <Tab label="Privacy Policy" {...a11yProps(1)} />
              <Tab label="Terms & Conditions" {...a11yProps(2)} />
              <Tab label="About" {...a11yProps(3)} />
              <Tab label="FAQ's" {...a11yProps(4)} />

            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form">
            <div className="gap_p">
              <div className="control_group w_50">
                <label htmlFor="email">Email Address</label>
                <TextField
                  id="email"
                  placeholder="Enter here"
                  fullWidth
                  hiddenLabel
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError("email");
                  }}
                  error={!!errors.email}
                />
                {errors.email && (
                  <h6 className="err_msg">{errors.email}</h6>
                )}
              </div>
              <div className="control_group w_50">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  country={"in"}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  inputStyle={{ width: "100%" }}
                  value={countryCode.replace("+", "") + phone}
                  onChange={(value, country) => {
                    const dialCode = (country as any)?.dialCode ? `+${(country as any).dialCode}` : "+91";
                    setCountryCode(dialCode);

                    // Extract phone number without country code
                    const countryCodeDigits = dialCode.replace("+", "");
                    const phoneDigits = value.replace(/\D/g, "");
                    const phoneWithoutCode = phoneDigits.startsWith(countryCodeDigits)
                      ? phoneDigits.substring(countryCodeDigits.length)
                      : phoneDigits;

                    setPhone(phoneWithoutCode);
                    clearError("phone");
                  }}
                />
                {errors.phone && (
                  <h6 className="err_msg">{errors.phone}</h6>
                )}
              </div>
            </div>
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                onClick={handleContactSupportSave}
                disabled={isUpdating || isLoading}
              >
                Save
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <EditText content={privacyPolicy} setContent={setPrivacyPolicy} />
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handlePrivacyPolicySave}
              disabled={isUpdating || isLoading}
            >
              Save
            </Button>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <EditText content={termsAndConditions} setContent={setTermsAndConditions} />
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleTermsAndConditionsSave}
              disabled={isUpdating || isLoading}
            >
              Save
            </Button>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>
          <EditText content={aboutUs} setContent={setAboutUs} />
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleAboutUsSave}
              disabled={isUpdating || isLoading}
            >
              Save
            </Button>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={4}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search..."
              />
            </div>
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setIsEdit(false);
                  setSelectedFaq(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> Add FAQ
              </Button>
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Sr.no</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell>Answer</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {faqData?.data?.list && faqData.data.list.length > 0 ? (
                  faqData.data.list.map((faq, index) => (
                    <TableRow hover key={faq._id}>
                      <TableCell>{(faqPage - 1) * faqRowsPerPage + index + 1}</TableCell>
                      <TableCell>{faq.question}</TableCell>
                      <TableCell className="max_c2">
                        <p>{faq.answer}</p>
                      </TableCell>
                      <TableCell>{moment(faq.createdAt).format("DD MMM YYYY")}</TableCell>
                      <TableCell>
                        <div className="table_actions v2">
                          <IconButton onClick={() => handleEditFaq(faq)}>
                            <img src="/static/images/edit2_icon.svg" alt="" />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteFaqClick(faq)}>
                            <img src="/static/images/trash2_icon.svg" alt="" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No FAQs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {faqData?.data?.totalCount && faqData.data.totalCount > 0 && (
            <div className="pagination_wrapper" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Pagination
                module="faq"
                page={faqPage}
                onPageChange={(newPage) => setFaqPage(newPage)}
                totalPages={Math.ceil(faqData.data.totalCount / faqRowsPerPage)}
                totalCount={faqData.data.totalCount}
                setPage={setFaqPage}
                limit={faqRowsPerPage}
              />
            </div>
          )}
        </CustomTabPanel>

      </div>

      <AddFaq
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        isEdit={isEdit}
        data={selectedFaq}
      />
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        setOpen={setDeleteModalOpen}
        onConfirm={handleConfirmDeleteFaq}
        itemName="this FAQ"
        isLoading={isDeletingFaq}
      />
    </>
  );
};

export default ManageCms;
